import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Send } from "lucide-react";
import { z } from "zod";

const PRODUCT_TYPES = [
  "ARIA Sculpt Bathtub",
  "ARIA Monolith Tub",
  "ARIA Diamond Bathtub",
  "VENA Counter Basin",
  "VENA Vanity Console",
  "Custom / Mixed Selection",
];

const FINISHES = [
  "Matte White",
  "Gloss White",
  "Custom RAL Color",
  "Textured (Fluted / Diamond / Ribbed)",
  "Undecided — Need Advice",
];

const QUANTITY_RANGES = [
  "10 – 49 units",
  "50 – 99 units",
  "100 – 249 units",
  "250 – 499 units",
  "500+ units",
];

const TIMELINES = [
  "ASAP (Air Freight 10–15 days)",
  "1 – 3 months",
  "3 – 6 months",
  "6 – 12 months",
  "Planning / Exploring",
];

const schema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  company: z.string().trim().max(150).optional(),
  phone: z.string().trim().max(40).optional(),
  notes: z.string().trim().max(1500).optional(),
});

export const MaterialsQuoteForm = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    product_type: PRODUCT_TYPES[0],
    finish: FINISHES[0],
    quantity: QUANTITY_RANGES[0],
    timeline: TIMELINES[1],
    notes: "",
  });

  const update =
    (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({
      name: form.name,
      email: form.email,
      company: form.company,
      phone: form.phone,
      notes: form.notes,
    });
    if (!parsed.success) {
      toast({
        title: "Check your details",
        description: parsed.error.issues[0]?.message ?? "Please review the form.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      const sessionId = window.crypto?.randomUUID?.() ?? `mat-${Date.now()}`;
      const composedMessage = [
        `Product: ${form.product_type}`,
        `Finish/Color: ${form.finish}`,
        `Quantity (MOQ): ${form.quantity}`,
        `Timeline: ${form.timeline}`,
        form.notes && `\nNotes:\n${form.notes}`,
      ]
        .filter(Boolean)
        .join("\n");

      const { error } = await supabase.functions.invoke("save-lead", {
        body: {
          name: form.name,
          email: form.email,
          phone: form.phone || undefined,
          company: form.company || undefined,
          interest_type: `Bath & Sanitary — Materials Quote — ${form.product_type}`,
          message: composedMessage,
          session_id: sessionId,
        },
      });
      if (error) throw error;
      setDone(true);
      toast({
        title: "Quote request received",
        description: "A specialist will reply with MOQ pricing within 1 business day.",
      });
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      toast({
        title: "Submission failed",
        description: "Please try again or email info@aureum7trading.com.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border border-amber-500 bg-white p-10 text-center">
        <h3 className="font-serif text-2xl font-semibold text-neutral-900">Quote request received</h3>
        <p className="mt-3 text-neutral-700">
          A solid surface specialist will reach out at{" "}
          <span className="font-medium text-amber-600">{form.email}</span> within 1 business day with
          MOQ pricing and lead times.
        </p>
      </div>
    );
  }

  const inputCls = "mt-1 bg-white border-neutral-300 text-neutral-900";
  const selectCls =
    "mt-1 flex h-10 w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900";

  return (
    <form
      onSubmit={handleSubmit}
      className="grid gap-4 border-2 border-amber-500 bg-white p-6 sm:p-8"
      aria-labelledby="materials-quote-heading"
    >
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Materials Quote</p>
        <h3 id="materials-quote-heading" className="mt-1 font-serif text-2xl font-bold text-neutral-900">
          Get MOQ Pricing for Solid Surface Bathware
        </h3>
        <div className="mt-2 h-px w-12 bg-amber-500" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="mq-name" className="text-neutral-700">Name *</Label>
          <Input id="mq-name" required maxLength={100} value={form.name} onChange={update("name")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="mq-company" className="text-neutral-700">Company</Label>
          <Input id="mq-company" maxLength={150} value={form.company} onChange={update("company")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="mq-email" className="text-neutral-700">Email *</Label>
          <Input id="mq-email" type="email" required maxLength={255} value={form.email} onChange={update("email")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="mq-phone" className="text-neutral-700">Phone / WhatsApp</Label>
          <Input id="mq-phone" maxLength={40} value={form.phone} onChange={update("phone")} className={inputCls} />
        </div>

        <div>
          <Label htmlFor="mq-product" className="text-neutral-700">Product type *</Label>
          <select id="mq-product" value={form.product_type} onChange={update("product_type")} className={selectCls}>
            {PRODUCT_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="mq-finish" className="text-neutral-700">Finish / Color *</Label>
          <select id="mq-finish" value={form.finish} onChange={update("finish")} className={selectCls}>
            {FINISHES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="mq-qty" className="text-neutral-700">Quantity (MOQ) *</Label>
          <select id="mq-qty" value={form.quantity} onChange={update("quantity")} className={selectCls}>
            {QUANTITY_RANGES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="mq-timeline" className="text-neutral-700">Project timeline *</Label>
          <select id="mq-timeline" value={form.timeline} onChange={update("timeline")} className={selectCls}>
            {TIMELINES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div>
        <Label htmlFor="mq-notes" className="text-neutral-700">Additional notes</Label>
        <Textarea
          id="mq-notes"
          rows={3}
          maxLength={1500}
          placeholder="Project location, OEM/branding needs, custom dimensions…"
          value={form.notes}
          onChange={update("notes")}
          className={inputCls}
        />
      </div>

      <Button type="submit" disabled={submitting} size="lg" className="bg-neutral-900 text-white hover:bg-neutral-800">
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        {submitting ? "Sending…" : "Request MOQ Quote"}
      </Button>
      <p className="text-xs text-neutral-500">
        Sent to <span className="font-medium text-amber-600">info@aureum7trading.com</span> · Reply within 1 business day.
      </p>
    </form>
  );
};

export default MaterialsQuoteForm;
